import { GQL } from '@aline/graphql';
import type {
  InsertNodeOperation,
  Node,
  NodeEntry,
  Operation,
  RemoveNodeOperation,
  SetNodeOperation,
  SetSelectionOperation,
  SplitNodeOperation,
} from 'slate';
import { Editor, Element, Text } from 'slate';

import type {
  AlineMarkerElement,
  BlockInstructionFieldElement,
  BookmarkEndElement,
  BookmarkMarkerElement,
  BookmarkStartElement,
  CheckboxElement,
  CheckboxOptionFieldElement,
  CrossReferenceElement,
  DrawingElement,
  DynamicContentElement,
  DynamicInlineElement,
  InstructionFieldElement,
  NonSigFieldElement,
  SuggestionMarkerElement,
  SuggestionMarkerEndElement,
  SuggestionMarkerStartElement,
} from './elements';
import {
  bookmarkEndType,
  bookmarkStartType,
  checkboxType,
  drawingType,
  dynamicBlockType,
  suggestionMarkerEndType,
  suggestionMarkerStartType,
  tableBorderSizes,
} from './elements';
import type {
  DeleteParagraphEdit,
  InsertParagraphEdit,
  SuggestedParagraphGlyphEdit,
} from './suggested-edits/suggested-paragraph-edits';
import type {
  InsertTableCellEdit,
  RemoveTableCellEdit,
  TableCellFormatEdit,
  TableCellMarginsEdit,
  TableCellShadeEdit,
} from './suggested-edits/suggested-table-cell-edits';
import type { RequireSome } from '../../types';
import type {
  AlignmentEdit,
  AlineBlockElement,
  AlineContentBlock,
  AlineDescendant,
  AlineEditor,
  AlineElement,
  AlineFontName,
  AlineInlineElement,
  AlineText,
  AnyFormatEdit,
  AnyInsertEdit,
  AnyRemoveEdit,
  BoldEdit,
  BreakElement,
  CannedFieldElement,
  ContentNodes,
  DateFieldElement,
  DeleteEdit,
  DeleteEditEntry,
  DocEditEntry,
  DocumentElement,
  DropdownFieldElement,
  DynamicBlockElement,
  DynamicContentType,
  DynamicElement,
  EditEntry,
  EditEntryGroup,
  EditEntryOrGroup,
  FirstLineIndentEdit,
  FontColorEdit,
  FontSizeEdit,
  FontTypeEdit,
  FooterElement,
  FootersElement,
  FootersMarginEdit,
  HeaderElement,
  HeaderMarginEdit,
  HeadersElement,
  HighlightEdit,
  IndentationLeftEdit,
  IndentationRightEdit,
  InlineInstructionFieldElement,
  InputFieldElement,
  InputFieldType,
  InsertBookmarkEdit,
  InsertBookmarkEditEntry,
  InsertBreakEdit,
  InsertBreakEditEntry,
  InsertCheckboxEdit,
  InsertCheckboxEditEntry,
  InsertDrawingEdit,
  InsertDrawingEditEntry,
  InsertEdit,
  InsertEditEntry,
  InsertInstructionFieldEdit,
  InsertInstructionFieldEditEntry,
  InsertTabEdit,
  InsertTabEditEntry,
  InsertTableRowEdit,
  ItalicEdit,
  LineSpacingEdit,
  LinkElement,
  LinkURLEdit,
  ListItemEdit,
  ListItemElement,
  ListLevel,
  LocationIdElement,
  NumberFieldElement,
  PageDimensionsEdit,
  PageFormatEdit,
  ParagraphBordersEdit,
  ParagraphElement,
  ParagraphProps,
  ParagraphShadeEdit,
  RadioButtonFieldElement,
  RemoveBreakEdit,
  RemoveCheckboxEdit,
  RemoveCheckboxEditEntry,
  RemoveDrawingEdit,
  RemoveInstructionFieldEdit,
  RemoveLinkEdit,
  RemoveTabEdit,
  RemoveTableRowEdit,
  RenderModelCheckboxOption,
  RenderModelContentBlock,
  RenderModelDrawing,
  RenderModelDynamicBlock,
  RenderModelElement,
  RenderModelEmptySplitNodeText,
  RenderModelFooter,
  RenderModelFootersElement,
  RenderModelHeader,
  RenderModelHeadersElement,
  RenderModelInline,
  RenderModelInlineInstructionField,
  RenderModelInputField,
  RenderModelLine,
  RenderModelLineChild,
  RenderModelList,
  RenderModelNode,
  RenderModelPage,
  RenderModelParagraph,
  RenderModelSignatureField,
  RenderModelTab,
  RenderModelTable,
  RenderModelTableCell,
  RenderModelTableRow,
  RenderModelText,
  RenderModelTOC,
  RMContainerNode,
  SelBoundaryNode,
  SignatureFieldElement,
  SmallCapsEdit,
  SpacingAfterEdit,
  SpacingBeforeEdit,
  StrikethroughEdit,
  StyleSheetEdit,
  StyleVariantEdit,
  SuggestedEdit,
  TabElement,
  TableAlignEdit,
  TableBorderSize,
  TableCellElement,
  TableElement,
  TableFormatEdit,
  TableIndentEdit,
  TableRowElement,
  TableShadeEdit,
  TextFieldElement,
  TextFormatEdit,
  TextFormatEditEntry,
  TOCElement,
  ToggleCheckboxEdit,
  UnderlineEdit,
  VerticalAlignEdit,
} from '../internal';
import {
  breakType,
  BreakVariant,
  cannedFieldType,
  dateFieldVariant,
  docEditKeys,
  documentType,
  editCategories,
  footersType,
  FooterType,
  headersType,
  HeaderType,
  inputFieldType,
  instructionFieldElementType,
  lineType,
  linkType,
  listType,
  numberFieldVariant,
  pageType,
  paragraphType,
  Queries,
  tableCellType,
  tableRowType,
  tableType,
  tabType,
  textFieldVariant,
} from '../internal';

export const alineFontTypeRecord: Record<AlineFontName, string> = {
  Aptos: '',
  'Aptos Display': '',
  'Aptos Mono': '',
  'Aptos Narrow': '',
  'Aptos Serif': '',
  Arial: '',
  Calibri: '',
  Cambria: '',
  'Century Gothic': '',
  'Courier New': '',
  'Franklin Gothic Medium': '',
  'Free Serif': '',
  Garamond: '',
  Georgia: '',
  Helvetica: '',
  'Helvetica Neue': '',
  'Lucida Sans Typewriter': '',
  'Open Sans': '',
  Palatino: '',
  Symbol: '',
  Tahoma: '',
  'Times New Roman': '',
  Verdana: '',
};

/**
 * Checks if the editor has `type === 'AlineEditor'`. Note that this is true for
 * BOTH `AlineEditor` (content model) and `RenderModelEditor` (render model).
 */
export function isAlineEditor(editor: Editor): editor is Editor {
  return editor.type === 'AlineEditor';
}

/**
 * Checks if the editor is specifically an `AlineEditor` (content model) as
 * opposed to a `RenderModelEditor`. The content model has `DocParts` as
 * children, while the render model has render-specific elements with
 * `relativePath` properties.
 *
 * Use this when you need to ensure the editor's children are `DocParts`
 * (a `DocPart[]` of document, headers, and footers in any order).
 */
export function isAlineContentEditor(editor: Editor): editor is AlineEditor {
  return (
    editor.type === 'AlineEditor' &&
    editor.children.length > 0 &&
    !('relativePath' in editor.children[0])
  );
}

export function isAlineFontName(name: string): name is AlineFontName {
  return Object.keys(alineFontTypeRecord).some(key => key === name);
}

export function isTableBorderSize(tableBorderSize: number): tableBorderSize is TableBorderSize {
  return tableBorderSizes.some(size => size === tableBorderSize);
}

export const isTableFormatEdit = (edit: SuggestedEdit): edit is TableFormatEdit =>
  edit.type === 'tableFormat';

export const isTableAlignEdit = (edit: SuggestedEdit): edit is TableAlignEdit => {
  return edit.type === 'tableFormat' && edit.value.align !== undefined;
};

export const isTableIndentEdit = (edit: SuggestedEdit): edit is TableIndentEdit => {
  return edit.type === 'tableFormat' && edit.value.indent !== undefined;
};

export const isTableShadeEdit = (edit: SuggestedEdit): edit is TableShadeEdit => {
  return edit.type === 'tableFormat' && edit.value.shade !== undefined;
};

export const isTableCellShadeEdit = (edit: SuggestedEdit): edit is TableCellShadeEdit => {
  return edit.type === 'tableCellFormat' && edit.value.shade !== undefined;
};

/**
 * A paragraph-mark edit — an `insertParagraph` (split) or `deleteParagraph`
 * (merge) suggestion stored on `paragraph.glyphProps.suggestedEdits`. See ECMA-376 §17.5.2.7.
 */
export const isParagraphGlyphEdit = (edit: SuggestedEdit): edit is SuggestedParagraphGlyphEdit =>
  edit.type === 'insertParagraph' || edit.type === 'deleteParagraph';

export function isDocumentElement(node: Node): node is DocumentElement {
  return (node as DocumentElement).type === documentType;
}

export function isFootersElement(node: Node): node is FootersElement {
  return (node as FootersElement).type === footersType;
}
export function isRMFootersElement(node: Node): node is RenderModelFootersElement {
  return (node as RenderModelFootersElement).type === footersType;
}

export function isHeadersElement(node: Node): node is HeadersElement {
  return (node as HeadersElement).type === headersType;
}
export function isRMHeadersElement(node: Node): node is RenderModelHeadersElement {
  return (node as RenderModelHeadersElement).type === headersType;
}

export function isLinkElement(node: Node): node is LinkElement {
  return (node as LinkElement).type === linkType;
}

export function nodeIsSelBoundary(node: Node): node is SelBoundaryNode {
  return isDocumentElement(node) || isFooterElement(node) || isHeaderElement(node);
}

/**
 * Below are type guards migrated from Client.
 */
export const isRemoveNodeOperation = (operation: Operation): operation is RemoveNodeOperation =>
  operation.type === 'remove_node';

export const isInsertNodeOperation = (operation: Operation): operation is InsertNodeOperation =>
  operation.type === 'insert_node';

export const isSetNodeOperation = (operation: Operation): operation is SetNodeOperation =>
  operation.type === 'set_node';

export const isSetSelectionOperation = (operation: Operation): operation is SetSelectionOperation =>
  operation.type === 'set_selection';

export const isSplitNodeOperation = (operation: Operation): operation is SplitNodeOperation =>
  operation.type === 'split_node';

export const isAlineElement = (node: Node): node is AlineElement =>
  (node as AlineElement).type !== undefined;

export const isAlineBlock = (editor: Editor, node: Node): node is AlineBlockElement =>
  isAlineElement(node) && Editor.isBlock(editor, node);

export const isTOCElement = (node: Node): node is TOCElement =>
  isAlineElement(node) && node.type === instructionFieldElementType && node.instruction === 'TOC';

export const isAlineContentBlock = (node: Node): node is AlineContentBlock => {
  const contentBlockTypes: AlineElement['type'][] = [tableType, paragraphType, dynamicBlockType];

  return isAlineElement(node) && (contentBlockTypes.includes(node.type) || isTOCElement(node));
};

export const isLocationIdElement = (node: Node): node is LocationIdElement =>
  isAlineContentBlock(node) || isTableRowElement(node) || isTableCellElement(node);

export const isRenderModelContentBlock = (node: Node): node is RenderModelContentBlock =>
  isAlineContentBlock(node);

export const isContentBlockNodes = (
  editor: Editor,
  nodes: ContentNodes
): nodes is AlineContentBlock[] => isAlineBlock(editor, nodes[0]);

export const isAlineDescendant = (node: Node): node is AlineDescendant =>
  isAlineElement(node) || Text.isText(node);

// === Dynamic Block ===

export function isDynamicBlockElement(node: Node): node is DynamicBlockElement {
  return Element.isElement(node) && node.type === 'dynamic-block';
}

export function isDynamicInlineElement(node: Node): node is DynamicInlineElement {
  return Element.isElement(node) && node.type === 'dynamic-inline';
}

export function isDynamicContentElement(node: Node): node is DynamicContentElement {
  return isDynamicBlockElement(node) || isDynamicInlineElement(node);
}

export function isDynamicContentType(
  type: DynamicContentType | InputFieldType
): type is DynamicContentType {
  return type !== 'INPUT';
}

// === Input Fields ===

export type ImageElement = SignatureFieldElement | DrawingElement;

export const isInputFieldElement = (node: Node): node is InputFieldElement =>
  (node as InputFieldElement).type === inputFieldType;

export const isCannedFieldElement = (node: Node): node is CannedFieldElement =>
  (node as CannedFieldElement).type === cannedFieldType;

export const isCannedFieldEntry = (entry: NodeEntry): entry is NodeEntry<CannedFieldElement> =>
  isCannedFieldElement(entry[0]);

export const isSignatureFieldElement = (node: Node): node is SignatureFieldElement =>
  isInputFieldElement(node) &&
  (node as SignatureFieldElement).variant === GQL.FieldVariant.Signature;

export const isNonSignatureFieldElement = (node: Node): node is NonSigFieldElement =>
  isInputFieldElement(node) && !isSignatureFieldElement(node);

export const isDateFieldElement = (node: Node): node is DateFieldElement =>
  (node as DateFieldElement).variant === dateFieldVariant;

export const isTextFieldElement = (node: Node): node is TextFieldElement =>
  (node as TextFieldElement).variant === textFieldVariant;

export const isNumberFieldElement = (node: Node): node is NumberFieldElement =>
  (node as NumberFieldElement).variant === numberFieldVariant;

export const isTableElement = (node: Node): node is TableElement =>
  (node as TableElement).type === tableType;

export function isTableRowElement(node: Node): node is TableRowElement {
  return (node as TableRowElement).type === tableRowType;
}

export function isTableCellElement(node: Node): node is TableCellElement {
  return (node as TableCellElement).type === tableCellType;
}

export function isRMTableCellElement(node: RenderModelNode): node is RenderModelTableCell {
  return (node as RenderModelTableCell).type === tableCellType;
}

export const isDrawingElement = (node: Node): node is DrawingElement =>
  (node as DrawingElement).type === drawingType;

export const isImageElement = (node: Node): node is ImageElement =>
  isDrawingElement(node) || isSignatureFieldElement(node);

export function isDynamicElement(node: Node): node is DynamicElement {
  return isInputFieldElement(node) || isDynamicContentElement(node);
}

export function isDynamicEntry(entry: NodeEntry): entry is NodeEntry<DynamicElement> {
  return isDynamicElement(entry[0]);
}

export const isBreakElement = (node: Node): node is BreakElement =>
  (node as BreakElement).type === breakType;

export const isCheckboxElement = (node: Node): node is CheckboxElement =>
  Element.isElement(node) && node.type === checkboxType;

export const isCheckboxOptionFieldElement = (node: Node): node is CheckboxOptionFieldElement =>
  Element.isElement(node) &&
  node.type === inputFieldType &&
  node.variant === GQL.FieldVariant.Checkbox;

export const isPageBreakElement = (node: Node): node is BreakElement =>
  isBreakElement(node) && node.variant === BreakVariant.Page;

export const isBlockInstructionFieldElement = (node: Node): node is BlockInstructionFieldElement =>
  (node as BlockInstructionFieldElement).type === instructionFieldElementType &&
  ['TOC'].includes((node as BlockInstructionFieldElement).instruction);

export const isInlineInstructionFieldElement = (
  node: Node
): node is InlineInstructionFieldElement =>
  (node as InlineInstructionFieldElement).type === instructionFieldElementType &&
  ['PAGE', 'NUMPAGES', 'REF', 'PAGEREF', 'NOTEREF'].includes(
    (node as InlineInstructionFieldElement).instruction
  );

export const isInstructionFieldElement = (node: Node): node is InstructionFieldElement =>
  isInlineInstructionFieldElement(node) || isBlockInstructionFieldElement(node);

export const isCrossReferenceElement = (node: Node): node is CrossReferenceElement =>
  (node as InlineInstructionFieldElement).type === instructionFieldElementType &&
  ['REF', 'PAGEREF', 'NOTEREF'].includes((node as InlineInstructionFieldElement).instruction);

export const isPageNumberingElement = (node: Node) =>
  Element.isElement(node) &&
  node.type === instructionFieldElementType &&
  ['PAGE', 'NUMPAGES'].includes((node as InlineInstructionFieldElement).instruction);

export const isTabElement = (node: Node): node is TabElement =>
  (node as TabElement).type === tabType;

export const isBookmarkStartElement = (node: Node): node is BookmarkStartElement =>
  (node as BookmarkStartElement).type === bookmarkStartType;

export const isBookmarkEndElement = (node: Node): node is BookmarkEndElement =>
  (node as BookmarkEndElement).type === bookmarkEndType;

export const isBookmarkMarkerElement = (node: Node): node is BookmarkMarkerElement =>
  isBookmarkStartElement(node) || isBookmarkEndElement(node);

export const isSuggestionMarkerStartElement = (node: Node): node is SuggestionMarkerStartElement =>
  (node as SuggestionMarkerStartElement).type === suggestionMarkerStartType;

export const isSuggestionMarkerEndElement = (node: Node): node is SuggestionMarkerEndElement =>
  (node as SuggestionMarkerEndElement).type === suggestionMarkerEndType;

export const isSuggestionMarkerElement = (node: Node): node is SuggestionMarkerElement =>
  isSuggestionMarkerStartElement(node) || isSuggestionMarkerEndElement(node);

/**
 * True if the op inserts/removes a suggestion marker or a text node carrying
 * `suggestedEdits`, or mutates `suggestedEdits` on an existing node. Used as a
 * fast wake-up signal for the suggestions toolbar so it can appear and
 * update its count as soon as redlines stream in, without waiting for the
 * coarser `docContentChangedCount` debounce.
 */
export function opTouchesSuggestion(op: Operation): boolean {
  if (op.type === 'insert_node' || op.type === 'remove_node') {
    if (isSuggestionMarkerElement(op.node)) return true;
    if (Text.isText(op.node) && (op.node.suggestedEdits?.length ?? 0) > 0) return true;
    return false;
  }
  if (op.type === 'set_node') {
    return 'suggestedEdits' in op.properties || 'suggestedEdits' in op.newProperties;
  }
  return false;
}

export function opsIncludeSuggestionOp(operations: readonly Operation[]): boolean {
  return operations.some(opTouchesSuggestion);
}

export const isMarkerElement = (node: Node): node is AlineMarkerElement =>
  isBookmarkMarkerElement(node) || isSuggestionMarkerElement(node);

export const isInvisibleElement = (node: Node): boolean => isMarkerElement(node);

export const isAlineInlineElement = (node: Node): node is AlineInlineElement =>
  isLinkElement(node) ||
  isInputFieldElement(node) ||
  isCannedFieldElement(node) ||
  isCheckboxElement(node) ||
  isCheckboxOptionFieldElement(node) ||
  isBreakElement(node) ||
  isDrawingElement(node) ||
  isTabElement(node) ||
  isMarkerElement(node) ||
  isInlineInstructionFieldElement(node);

export const isRenderModelDrawing = (node: Node): node is RenderModelDrawing =>
  (node as RenderModelDrawing).type === drawingType;

export const isRenderModelDynamicBlock = (node: Node): node is RenderModelDynamicBlock =>
  (node as RenderModelDynamicBlock).type === dynamicBlockType;

export const isRenderModelLine = (node: Node): node is RenderModelLine =>
  (node as RenderModelLine).type === lineType;

export const isRenderModelLineChild = (editor: Editor, node: Node): node is RenderModelLineChild =>
  Text.isText(node) || (Element.isElement(node) && Editor.isInline(editor, node));

export const isRenderModelList = (node: Node): node is RenderModelList =>
  (node as RenderModelList).type === listType;

export const isRenderModelParagraph = (node: Node): node is RenderModelParagraph =>
  (node as RenderModelParagraph).type === paragraphType;

export const isRenderModelPage = (node: Node): node is RenderModelPage =>
  (node as RenderModelPage).type === pageType;

export const isRMContainerNode = (node: Node): node is RMContainerNode =>
  isRenderModelPage(node) || isRMHeaderElement(node) || isRMFooterElement(node);

export const isRenderModelTab = (node: Node): node is RenderModelTab =>
  (node as RenderModelTab).type === tabType;

export const isRenderModelTable = (node: Node): node is RenderModelTable =>
  (node as RenderModelTable).type === tableType;

export const isRenderModelTableRow = (node: Node): node is RenderModelTableRow =>
  (node as RenderModelTableRow).type === tableRowType;

export const isRenderModelTableCell = (node: Node): node is RenderModelTableCell =>
  (node as RenderModelTableCell).type === tableCellType;

export const isRenderModelInlineInstructionField = (
  node: Node
): node is RenderModelInlineInstructionField => isInlineInstructionFieldElement(node);

export const isRenderModelCheckboxOption = (node: Node): node is RenderModelCheckboxOption =>
  isCheckboxOptionFieldElement(node);

export const isRenderModelTOC = (node: Node): node is RenderModelTOC => isTOCElement(node);

export const isRenderModelDynamicBlockEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelDynamicBlock> => isRenderModelDynamicBlock(nodeEntry[0]);

export const isRenderModelTableEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelTable> => isRenderModelTable(nodeEntry[0]);

export const isRenderModelTableRowEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelTableRow> => isRenderModelTableRow(nodeEntry[0]);

export const isRenderModelTableCellEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelTableCell> => isRenderModelTableCell(nodeEntry[0]);

export const isRenderModelInlineInstructionFieldEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelInlineInstructionField> =>
  isRenderModelInlineInstructionField(nodeEntry[0]);

export const isRenderModelCheckboxOptionEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelCheckboxOption> => isRenderModelCheckboxOption(nodeEntry[0]);

export const isRenderModelTOCEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelTOC> => isRenderModelTOC(nodeEntry[0]);

export const isRenderModelPageEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelPage> => isRenderModelPage(nodeEntry[0]);

export const isRenderModelParagraphEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelParagraph> => isRenderModelParagraph(nodeEntry[0]);

export function isFooterElement(node: Node): node is FooterElement {
  return Object.values(FooterType).includes((node as FooterElement).type);
}

export function isHeaderElement(node: Node): node is HeaderElement {
  return Object.values(HeaderType).includes((node as HeaderElement).type);
}

export function isRMFooterElement(node: Node): node is RenderModelFooter {
  return Object.values(FooterType).includes((node as FooterElement).type);
}

export function isRMHeaderElement(node: Node): node is RenderModelHeader {
  return Object.values(HeaderType).includes((node as HeaderElement).type);
}

export const isParagraphElement = (node: Node | ParagraphProps): node is ParagraphElement =>
  (node as ParagraphElement).type === paragraphType;

export const isListItem = (node: Node | ParagraphProps): node is ListItemElement =>
  isParagraphElement(node) && !!Queries.renderedParagraphListProps(node);

export const isListLevel = (listLevel: number | null): listLevel is ListLevel =>
  listLevel !== null && [0, 1, 2, 3, 4, 5, 6, 7, 8].includes(listLevel);

export const isRenderModelInline = (node: RenderModelLineChild): node is RenderModelInline =>
  (node as RenderModelInline).children !== undefined;

export const isRenderModelLineEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelLine> =>
  (nodeEntry as NodeEntry<RenderModelLine>)[0].type === lineType;

export const isElementEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<Element> =>
  Element.isElement((nodeEntry as NodeEntry<Element>)[0]);

export const isInlineEntry = (
  editor: Editor,
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<AlineInlineElement> =>
  Editor.isInline(editor, (nodeEntry as NodeEntry<AlineInlineElement>)[0]);

export const isBookmarkMarkerEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<BookmarkMarkerElement> => isBookmarkMarkerElement(nodeEntry[0]);

export const isSuggestionMarkerEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<SuggestionMarkerElement> => isSuggestionMarkerElement(nodeEntry[0]);

export const isLinkEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<LinkElement> =>
  isLinkElement(nodeEntry[0]);

export const isCrossReferenceEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<CrossReferenceElement> => isCrossReferenceElement(nodeEntry[0]);

export const isParagraphEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<ParagraphElement> =>
  isParagraphElement(nodeEntry[0]);

export const isTableEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<TableElement> =>
  isTableElement(nodeEntry[0]);

export const isTableRowEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<TableRowElement> =>
  isTableRowElement(nodeEntry[0]);

export const isTableCellEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<TableCellElement> =>
  isTableCellElement(nodeEntry[0]);

export const isTOCEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<TOCElement> =>
  isTOCElement(nodeEntry[0]);

export const isDocumentEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<DocumentElement> =>
  isDocumentElement(nodeEntry[0]);

export const isFootersPartEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<FootersElement> =>
  isFootersElement(nodeEntry[0]);

export const isHeadersPartEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<HeadersElement> =>
  isHeadersElement(nodeEntry[0]);

export const isTextEntry = (nodeEntry: NodeEntry): nodeEntry is NodeEntry<AlineText> =>
  Text.isText(nodeEntry[0]);

export function getIsTextNodeWithComments(node: Node): node is AlineText {
  return Text.isText(node) && Array.isArray(node.comments) && !!node.comments.length;
}

export function getIsTextNodeWithoutComments(node: Node): node is AlineText {
  return Text.isText(node) && Array.isArray(node.comments) && !node.comments.length;
}

export type TextFieldEntry = NodeEntry<TextFieldElement>;
export const isTextFieldEntry = (nodeEntry?: NodeEntry | null): nodeEntry is TextFieldEntry => {
  if (!nodeEntry) return false;

  const [node] = nodeEntry;
  return Element.isElement(node) && node.type === 'INPUT' && node.variant === GQL.FieldVariant.Text;
};

export type NumberFieldEntry = NodeEntry<NumberFieldElement>;
export const isNumberFieldEntry = (nodeEntry?: NodeEntry | null): nodeEntry is NumberFieldEntry => {
  if (!nodeEntry) return false;

  const [node] = nodeEntry;
  return (
    Element.isElement(node) && node.type === 'INPUT' && node.variant === GQL.FieldVariant.Number
  );
};

export type DateFieldEntry = NodeEntry<DateFieldElement>;
export const isDateFieldEntry = (nodeEntry?: NodeEntry | null): nodeEntry is DateFieldEntry => {
  if (!nodeEntry) return false;

  const [node] = nodeEntry;
  return Element.isElement(node) && node.type === 'INPUT' && node.variant === GQL.FieldVariant.Date;
};

export type DropdownFieldEntry = NodeEntry<DropdownFieldElement>;
export const isDropdownFieldEntry = (
  nodeEntry?: NodeEntry | null
): nodeEntry is DropdownFieldEntry => {
  if (!nodeEntry) return false;

  const [node] = nodeEntry;
  return (
    Element.isElement(node) && node.type === 'INPUT' && node.variant === GQL.FieldVariant.Dropdown
  );
};

export type RadioButtonFieldEntry = NodeEntry<RadioButtonFieldElement>;
export const isRadioButtonFieldEntry = (
  nodeEntry?: NodeEntry | null
): nodeEntry is RadioButtonFieldEntry => {
  if (!nodeEntry) return false;

  const [node] = nodeEntry;
  return (
    Element.isElement(node) &&
    node.type === 'INPUT' &&
    node.variant === GQL.FieldVariant.Radiobutton
  );
};

export type SignatureFieldEntry = NodeEntry<SignatureFieldElement>;
export const isSignatureFieldEntry = (
  nodeEntry?: NodeEntry | null
): nodeEntry is SignatureFieldEntry => !!nodeEntry && isSignatureFieldElement(nodeEntry[0]);

export type InputFieldEntry = NodeEntry<InputFieldElement>;
export const isInputFieldEntry = (nodeEntry?: NodeEntry | null): nodeEntry is InputFieldEntry =>
  !!nodeEntry && isInputFieldElement(nodeEntry[0]);

export type NonSigFieldEntry = NodeEntry<NonSigFieldElement>;
export const isNonSignatureFieldEntry = (
  nodeEntry?: NodeEntry | null
): nodeEntry is NonSigFieldEntry => !!nodeEntry && isNonSignatureFieldElement(nodeEntry[0]);

export const isRenderModelInputFieldEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelInputField> =>
  isInputFieldEntry(nodeEntry) && 'relativePath' in nodeEntry[0];

export const isRenderModelSignatureFieldEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelSignatureField> =>
  isSignatureFieldEntry(nodeEntry) && 'relativePath' in nodeEntry[0];

export function isRenderModelEmptySplitNodeText(node: Node): node is RenderModelEmptySplitNodeText {
  return Text.isText(node) && !!(node as RenderModelEmptySplitNodeText).isEmptySplitNodeText;
}

export function isRenderModelText(node: Node): node is RenderModelText {
  return Text.isText(node) && (node as RenderModelText).relativeStartPoint !== undefined;
}

export function isRenderModelElement(node: RenderModelNode): node is RenderModelElement {
  return (node as RenderModelElement).type !== undefined;
}

export const isRenderModelTextEntry = (
  nodeEntry: NodeEntry
): nodeEntry is NodeEntry<RenderModelText> => {
  const [node] = nodeEntry;

  return isRenderModelText(node);
};

export function isRenderModelElementEntry(
  nodeEntry: NodeEntry<RenderModelNode>
): nodeEntry is NodeEntry<RenderModelElement> {
  const [node] = nodeEntry;

  return isRenderModelElement(node);
}

/**
 * Suggested edit type guards.
 */

export const isItalicEdit = (edit: SuggestedEdit): edit is ItalicEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.italic !== undefined;
};

export const isBoldEdit = (edit: SuggestedEdit): edit is BoldEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.bold !== undefined;
};

export const isUnderlineEdit = (edit: SuggestedEdit): edit is UnderlineEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.underline !== undefined;
};

export const isStrikethroughEdit = (edit: SuggestedEdit): edit is StrikethroughEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.strikethrough !== undefined;
};

export const isSmallCapsEdit = (edit: SuggestedEdit): edit is SmallCapsEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.smallCaps !== undefined;
};

export const isFontTypeEdit = (edit: SuggestedEdit): edit is FontTypeEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.fontType !== undefined;
};

export const isVerticalAlignEdit = (edit: SuggestedEdit): edit is VerticalAlignEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.verticalAlign !== undefined;
};

export const isFontSizeEdit = (edit: SuggestedEdit): edit is FontSizeEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.fontSize !== undefined;
};

export const isFontColorEdit = (edit: SuggestedEdit): edit is FontColorEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.fontColor !== undefined;
};

export const isHighlightEdit = (edit: SuggestedEdit): edit is HighlightEdit => {
  if (edit.type !== 'textFormat') return false;

  return edit.value.highlight !== undefined;
};

export const isAnyInsertEdit = (edit: SuggestedEdit): edit is AnyInsertEdit =>
  editCategories[edit.type] === 'insert';

export const isAnyRemoveEdit = (edit: SuggestedEdit): edit is AnyRemoveEdit =>
  editCategories[edit.type] === 'remove';

export const isAnyFormatEdit = (edit: SuggestedEdit): edit is AnyFormatEdit =>
  editCategories[edit.type] === 'format';

export const isDeleteEdit = (edit: SuggestedEdit): edit is DeleteEdit => edit.type === 'deleteNode';

export const isInsertEdit = (edit: SuggestedEdit): edit is InsertEdit => edit.type === 'insertNode';

export const isRemoveBreakEdit = (edit: SuggestedEdit): edit is RemoveBreakEdit =>
  edit.type === 'removeBreak';

export const isInsertBreakEdit = (edit: SuggestedEdit): edit is InsertBreakEdit =>
  edit.type === 'insertBreak';

export const isInsertCheckboxEdit = (edit: SuggestedEdit): edit is InsertCheckboxEdit =>
  edit.type === 'insertCheckbox';

export const isRemoveCheckboxEdit = (edit: SuggestedEdit): edit is RemoveCheckboxEdit =>
  edit.type === 'removeCheckbox';

export const isToggleCheckboxEdit = (edit: SuggestedEdit): edit is ToggleCheckboxEdit =>
  edit.type === 'toggleCheckbox';

export const isRemoveTabEdit = (edit: SuggestedEdit): edit is RemoveTabEdit =>
  edit.type === 'removeTab';

export const isInsertTabEdit = (edit: SuggestedEdit): edit is InsertTabEdit =>
  edit.type === 'insertTab';

export const isInsertBookmarkEdit = (edit: SuggestedEdit): edit is InsertBookmarkEdit =>
  edit.type === 'insertBookmark';

export const isInsertDrawingEdit = (edit: SuggestedEdit): edit is InsertDrawingEdit =>
  edit.type === 'insertDrawing';

export const isRemoveDrawingEdit = (edit: SuggestedEdit): edit is RemoveDrawingEdit =>
  edit.type === 'removeDrawing';

export const isTextFormatEdit = (edit: SuggestedEdit): edit is TextFormatEdit =>
  edit.type === 'textFormat';

export const isStyleVariantEdit = (edit: SuggestedEdit): edit is StyleVariantEdit =>
  edit.type === 'variant';

export const isAlignmentEdit = (edit: SuggestedEdit): edit is AlignmentEdit => {
  if (edit.type !== 'paragraphFormat') return false;

  return edit.value.alignment !== undefined;
};

export const isFirstLineIndentEdit = (edit: SuggestedEdit): edit is FirstLineIndentEdit => {
  if (edit.type !== 'paragraphFormat') return false;

  return edit.value.indentationFirstLine !== undefined;
};

export const isLeftIndentEdit = (edit: SuggestedEdit): edit is IndentationLeftEdit => {
  if (edit.type !== 'paragraphFormat') return false;

  return edit.value.indentationLeft !== undefined;
};

export const isParagraphShadeEdit = (edit: SuggestedEdit): edit is ParagraphShadeEdit => {
  return edit.type === 'paragraphFormat' && edit.value.shade !== undefined;
};

export const isParagraphBordersEdit = (edit: SuggestedEdit): edit is ParagraphBordersEdit => {
  return edit.type === 'paragraphFormat' && edit.value.borders !== undefined;
};

export const isRightIndentEdit = (edit: SuggestedEdit): edit is IndentationRightEdit => {
  if (edit.type !== 'paragraphFormat') return false;

  return edit.value.indentationRight !== undefined;
};

export const isLineSpacingEdit = (edit: SuggestedEdit): edit is LineSpacingEdit => {
  if (edit.type !== 'paragraphFormat') return false;

  return edit.value.lineSpacing !== undefined;
};

export const isSpacingAfterEdit = (edit: SuggestedEdit): edit is SpacingAfterEdit => {
  if (edit.type !== 'paragraphFormat') return false;

  return edit.value.spacingAfter !== undefined;
};

export const isSpacingBeforeEdit = (edit: SuggestedEdit): edit is SpacingBeforeEdit => {
  if (edit.type !== 'paragraphFormat') return false;

  return edit.value.spacingBefore !== undefined;
};

export const isStyleSheetEdit = (edit: SuggestedEdit): edit is StyleSheetEdit =>
  edit.type === 'styleSheet';

export const isPageDimensionsEdit = (edit: SuggestedEdit): edit is PageDimensionsEdit =>
  edit.type === 'pageDimensions';

export const isRemoveLinkEdit = (edit: SuggestedEdit): edit is RemoveLinkEdit =>
  edit.type === 'removeLink';

export const isLinkURLEdit = (edit: SuggestedEdit): edit is LinkURLEdit => edit.type === 'linkEdit';

export const isPageFormatEdit = (edit: SuggestedEdit): edit is PageFormatEdit =>
  (edit as PageFormatEdit).value.headerFooter !== undefined;

export const isFooterMarginEdit = (edit: SuggestedEdit): edit is FootersMarginEdit =>
  (edit as FootersMarginEdit).value.footerMargin !== undefined;

export const isHeaderMarginEdit = (edit: SuggestedEdit): edit is HeaderMarginEdit =>
  (edit as HeaderMarginEdit).value.headerMargin !== undefined;

export const isInsertInstructionFieldEdit = (
  edit: SuggestedEdit
): edit is InsertInstructionFieldEdit => edit.type === 'insertInstructionField';

export const isRemoveInstructionFieldEdit = (
  edit: SuggestedEdit
): edit is RemoveInstructionFieldEdit => edit.type === 'removeInstructionField';

export const isRemoveTableRowEdit = (edit: SuggestedEdit): edit is RemoveTableRowEdit =>
  edit.type === 'removeTableRow';

export const isRemoveTableCellEdit = (edit: SuggestedEdit): edit is RemoveTableCellEdit =>
  edit.type === 'removeTableCell';

export const isInsertTableRowEdit = (edit: SuggestedEdit): edit is InsertTableRowEdit =>
  edit.type === 'insertTableRow';

export const isInsertTableCellEdit = (edit: SuggestedEdit): edit is InsertTableCellEdit =>
  edit.type === 'insertTableCell';

export const isTableCellFormatEdit = (edit: SuggestedEdit): edit is TableCellFormatEdit =>
  edit.type === 'tableCellFormat';

export const isTableCellMarginsEdit = (edit: SuggestedEdit): edit is TableCellMarginsEdit => {
  return edit.type === 'tableCellFormat' && edit.value.margins !== undefined;
};

export const isDocEditEntry = (editEntry: EditEntry): editEntry is DocEditEntry =>
  'edit' in editEntry && docEditKeys.includes((editEntry as DocEditEntry).edit.type);

export const isTextFormatEditEntry = (editEntry: EditEntry): editEntry is TextFormatEditEntry =>
  isTextFormatEdit(editEntry.edit);

export const isInsertEditEntry = (editEntry: EditEntry): editEntry is InsertEditEntry =>
  isInsertEdit(editEntry.edit);

export const isInsertBreakEditEntry = (editEntry: EditEntry): editEntry is InsertBreakEditEntry =>
  isInsertBreakEdit(editEntry.edit);

export const isInsertCheckboxEditEntry = (
  editEntry: EditEntry
): editEntry is InsertCheckboxEditEntry => isInsertCheckboxEdit(editEntry.edit);

export const isRemoveCheckboxEditEntry = (
  editEntry: EditEntry
): editEntry is RemoveCheckboxEditEntry => isRemoveCheckboxEdit(editEntry.edit);

export const isInsertTabEditEntry = (editEntry: EditEntry): editEntry is InsertTabEditEntry =>
  isInsertTabEdit(editEntry.edit);

export const isInsertBookmarkEditEntry = (
  editEntry: EditEntry
): editEntry is InsertBookmarkEditEntry => isInsertBookmarkEdit(editEntry.edit);

export const isInsertInstructionFieldEditEntry = (
  editEntry: EditEntry
): editEntry is InsertInstructionFieldEditEntry => isInsertInstructionFieldEdit(editEntry.edit);

export const isInsertDrawingEditEntry = (
  editEntry: EditEntry
): editEntry is InsertDrawingEditEntry => isInsertDrawingEdit(editEntry.edit);

export const isDeleteEditEntry = (editEntry: EditEntry): editEntry is DeleteEditEntry =>
  isDeleteEdit(editEntry.edit);

export function isEditEntryGroup(entry: EditEntryOrGroup): entry is EditEntryGroup {
  return 'editEntries' in entry;
}

export const isListItemEdit = (edit: SuggestedEdit): edit is ListItemEdit => {
  if (edit.type !== 'paragraphFormat') return false;

  return edit.value.listProps !== undefined;
};

export function isInsertEditWithSource(
  edit: SuggestedEdit
): edit is RequireSome<InsertEdit | InsertParagraphEdit, 'source'> {
  return Boolean((edit.type === 'insertNode' || edit.type === 'insertParagraph') && edit.source);
}

/**
 * Any source stamp marks an AI-produced edit (chat or playbook) — an edit
 * without a source is a human suggestion.
 */
export function isAIInsertEdit(edit: SuggestedEdit) {
  return isInsertEditWithSource(edit);
}

export function isDeleteEditWithSource(
  edit: SuggestedEdit
): edit is RequireSome<DeleteEdit | DeleteParagraphEdit, 'source'> {
  return Boolean((edit.type === 'deleteNode' || edit.type === 'deleteParagraph') && edit.source);
}

export function isAIDeleteEdit(edit: SuggestedEdit) {
  return isDeleteEditWithSource(edit);
}

export function isInsertTableRowEditWithSource(
  edit: SuggestedEdit
): edit is RequireSome<InsertTableRowEdit, 'source'> {
  return Boolean(edit.type === 'insertTableRow' && edit.source);
}

export function isRemoveTableRowEditWithSource(
  edit: SuggestedEdit
): edit is RequireSome<RemoveTableRowEdit, 'source'> {
  return Boolean(edit.type === 'removeTableRow' && edit.source);
}

export function isRemoveTableCellEditWithSource(
  edit: SuggestedEdit
): edit is RequireSome<RemoveTableCellEdit, 'source'> {
  return Boolean(edit.type === 'removeTableCell' && edit.source);
}

export function isEditWithSource(
  edit: SuggestedEdit
): edit is RequireSome<
  | InsertEdit
  | DeleteEdit
  | InsertTableRowEdit
  | RemoveTableRowEdit
  | RemoveTableCellEdit
  | InsertParagraphEdit
  | DeleteParagraphEdit,
  'source'
> {
  return (
    isInsertEditWithSource(edit) ||
    isDeleteEditWithSource(edit) ||
    isInsertTableRowEditWithSource(edit) ||
    isRemoveTableRowEditWithSource(edit) ||
    isRemoveTableCellEditWithSource(edit)
  );
}

/**
 * Returns true if the node is a text node with an edit that has a source.
 */
export function isTextWithSuggestionSource(node: Node): node is Text {
  return Text.isText(node) && !!node.suggestedEdits && node.suggestedEdits.some(isEditWithSource);
}

// Apollo type guards.
export type ExcludesSignatureField<T> = Exclude<T, Pick<GQL.SignatureField, '__typename'>>;

export function isDynamicBlockEntry(
  nodeEntry: NodeEntry | null
): nodeEntry is NodeEntry<DynamicBlockElement> {
  if (!nodeEntry) return false;

  const [node] = nodeEntry;
  return Element.isElement(node) && node.type === 'dynamic-block';
}

/**
 * Check if a node is of a type to which a popover is sometimes attached.
 */
export function isNodeWithPopover(node: Node) {
  return isInputFieldElement(node) || isDynamicBlockElement(node) || isLinkElement(node);
}

/**
 * Check if a node is a void element that doesn't render its children as text.
 */
export function isLeafVoid(editor: Editor, node: Node): boolean {
  if (
    isNonSignatureFieldElement(node) ||
    isInlineInstructionFieldElement(node) ||
    isCannedFieldElement(node) ||
    isCrossReferenceElement(node)
  )
    return false;

  return Element.isElement(node) && Editor.isVoid(editor, node);
}
